/*
    * Root Index
*/
import Vue from "vue";
import Vuex, { StoreOptions } from "vuex";
import { RootApp } from './modules/app/index';
import { RootEditProfile } from './modules/app/Assets/EditProfile/index';
import { RootAbout } from './modules/app/Assets/About/index';
import { RootAnnouncement } from './modules/app/Assets/Announcement/index';
import document from './modules/app/Assets/document';
import dashboard from './modules/app/Assets/dashboard';
import tooltip from './modules/app/Assets/tooltip';
import workflow from './modules/app/Assets/workflow';
import getters from "./getters";
import actions from "./actions";
import { mutations } from "./mutations";
import modules from './modules/app/Module';
import { registration } from './modules/registration/store';
import { reviewForm } from './modules/reviewForm/store';
import { assessmentForm } from './modules/review.internal';
import { reviewExternal } from './modules/review.external';
import { application } from './modules/application';
import { conceptNote } from './modules/conceptNote';
import { grant } from './modules/grant';
import { technicalReport } from './modules/technicalReport';
import { financialReport } from './modules/financialReport';
import { communication } from './modules/communication';
import { tracker } from './modules/tracker'
import { feedback } from './modules/feedback';
import { validation } from './modules/Validation';
import { migration } from './modules/migration';
import { grantAmendment } from './modules/grantAmendment';

/*
    * Global Variables
*/
declare global {
    interface Window {
        area: string | null | undefined;
        controller: string | null | undefined;
        cardName: string | null | undefined;
        singlePage: boolean | false;
        modules: object;
        dashborad: boolean | false;
    }
}

Vue.use(Vuex);

const store: StoreOptions<any> = {
    strict: false,
    state: {
        switches: true,
        authorized: false,
        loggedInUser: '',
        avatar: '',
        applicationName: 'IUCN Grants Portal',
        
        /* --- A --- */
        accessDeny: false,
        ajaxFormSubmit: true,
        ajaxError: false,
        ajaxMessage: null,
        appTitle: null,
        autoOpenItems: [],
        auditReport: false,

        /* --- B --- */
        browserAutocomplete: false,
        buttonDisabled: false,
        buttonGroup: false,

        /* --- C --- */
        card: {
            type: 'Form'
        },
        childComponents: false,
        collapseOnScroll: true,
        columnIndex: null,
        contentHeader: null,
        contextMenu: true,
        contextMenuIndex: null,
        componentId: window.location.hash.substr(1),
        currency: {
            enabled: false,
            decimal: '.',
            thousands: ',',
            precision: 2,
            masked: false
        },
        currentOperation: {
            _create: 'Add',
            _update: 'Edit',
            _delete: 'Delete',
        },

        /* --- D --- */
        defaultDateFormat: 'DD/MM/YYYY',
        defaultDateWithTime: "DD/MM/YYYY h:mm:ss A",
        displayCurrentDate: false,
        downloadAction: process.env.VUE_APP_API_Base_URL + 'api/PanERPActions/GetDownloadFiles',
        downloadFiles: [],
        multiSelectKey: 'DocumentStoreId',

        /* --- E --- */
        emptyRowIndex: -1,
        enabledAdd: false,
        extraParams: null,
        exportOption: {
            enabled: true,
            xls: false,
            csv: false,
            xlsx: true,
            zip: false,
        },
        exportableFields: {},

        /* --- F --- */
        files: {
            userIcon: 'treeview-icon-user',
            panelIcon: 'panel-icon-user',
        },
        fixedHeader: false,
        formData: [],
        formCreate: false,
        formEdit: false,
        formGroups: [],
        formView: true,
        formulaTypes: {
            sum: "sum",
            average: "avg",
            min: "min",
            max: "max",
            count: "count",
            custom: "custom"
        },
        fullScreen: false,

        /* --- G --- */
        getTemplate: '/api/Dashboard/GetTemplate/',
        grandParent: null,
        grouping: {
            enabled: false,
            collapse: false,
            title: '',
            defaultGroupBy: []
        },

        /* --- H --- */
        headers: [],
        headerMaxLength: null,
        headerTooltip: '',
        hideButtons: [],
        hotkeys: {
            enabled: true
        },
        hyperLink: true,

        /* --- I --- */
        importOption: {
            enabled: false,
            previewDialog: false,
            processLogDialog: false,
            preview: false
        },
        inlineAddContextItems: {},
        inlineContextItems: {},
        inlineEditor: {
            enabled: false,
            emptyRow: false,
            autoSave: true,
        },
        invertedScroll: true,
        isDirty: false,
        isInUse: false,
        isSidePanel: true,
        items: {},
        showSaveFilter: false,
        saveAndContinue: true,
        showRowNumber: false,

        /* --- M --- */
        messages: {
            accessDenyText: 'You have limited permission to access',
            accessDenied: 'Access Denied',
            add: 'Add',
            edit: 'Edit',
            delete: 'Delete',
            close: 'Close',
            cancel: 'Cancel',
            save: 'Save',
            new: 'New',
            csvExport: 'CSV Export',
            excelExport: 'Excel Export',
            export: 'Export',
            expand: 'Expand',
            collapse: 'Collapse',
            dateSelection: 'Select Date',
            itemsPerPageText: 'Rows per page',
            pageText: '{0}-{1} of {2}',
            search: 'Search',
            saveContinue: 'Save & Continue',
            tooltipEditor: 'Tooltip Editor',
            cardFill: 'Please fill out required fields in the General Info Card and press the Save button.',
            changesConfirm: 'Your changes will be lost. Are you sure?',
            confirm: 'Confirm',
            dashboardClose: 'Are you sure you want to close?',
            deleteConfirm: 'Are you sure you want to delete this record?',
            download: 'Click here to download ',
            invalidDate: 'Invalid date',
            invalidDateMessage: 'Invalid date format. Please enter the date in the format ',
            loading: 'Loading...',
            noFileSelected: 'No file selected.',
            noResults: 'No matching records found',
            noData: 'No data available',
            rowFill: 'Kindly fill the previous row',
            success: 'Success',
            successCreate: 'The record is created successfully.',
            successUpdate: 'The record is updated successfully.',
            successDelete: 'The record is deleted successfully.',
            permissionWarn: 'You don\'t have sufficient permission for do this operation',
            callIsInUse: 'This call is already in use. Are you sure you want to continue?',
            import:'Import'
        },
        keyTranslator: true,
        moduleName: '',
        multiColumn: 'Auto',

        /* --- N --- */
        notyTypes: { error: 'error', info: 'info', success: 'success' },

        /* --- O --- */
        orginalFields: null,

        /* --- P --- */
        permission: { create: 'A', edit: 'E', delete: 'D' },
        permissions: '',
        placeholder: ' ',
        progress: false,
        progressCircular: false,

        /* --- R --- */
        renderComponent: false,
        renderTitleComponent: true,
        renderTitleSearchComponent: true, // using title search component button enabled or disabled
        redirect: null,
        required: false,
        rowMenuButtons: { enabled: false },
        activeRow: {},

        /* --- S --- */
        search: '',
        searchComponent: false,
        selecting: false,
        multiselect: false,
        selectedItems: [],
        simpleSearch: true,
        simplefilter: null,
        skipNullField: false,
        showModal: false,
        summaryPanel: {
            enabled: false,
            position: "bottom", // or bottom
            sticky: true
        },

        /* --- T --- */
        theme: {
            dense: false,
            dark: false, // default light color
            font: 'md',  // or sm, md,lg
            icon: 'md'  // or sm, md,lg
        },
        title: '',
        titleSearch: null,
        type: {
            _form: 'Form',
            _formNoLabel: 'FormNoLabel',
            _formLabelLeft: 'FormLabelLeft',
            _formLabelRight: 'FormLabelRight',
            _formLabelTop: 'FormLabelTop',
            _grid: 'Grid',
            _inlineEdit: 'Inline',
            _tree: 'Tree',
            _treeTable: 'TreeTable'
        },
        tooltipsFormData: [],
        tooltipsList: [],
        tooltipsDialog: false,
        treeTable: {
            collapse: false
        },

        /* --- Snackbar --- */
        color: 'success',
        mode: '',
        snackbar: false,
        text: '',
        timeout: 5000,
        y: 'top',
        x: null,

        /* --- Grid --- */
        loading: true,
        isMobile: false,
        selected: [],
        indeterminate: true,
        pagination: {
            enabled: true,
            itemsPerPage: 1,
            rowsPerPageItems: [],
            pageSize: 10,
            page: 1
        },

        /* --- Sidepanel --- */
        sidebar: {
            enabled: true,
            collapse: true,
            position: 'left'
        },
        sorting: {
            descending: true,
            sortBy: ''
        },

        /* --- Tree --- */
        autoOpen: false,
        custom: false,
        caseSensitive: {
            enabled: true,
            sensitive: true,
        },

        workflow: {
            workflowImplemented: false
        },

        /* --- Form --- */
        dialogPosition: 'center',
        dialog: false,
        landscape: false,
        renderDatepicker: true,
        editedIndex: -1,
        editedItem: {},
        mapItem: {},
        showInputs: true,
        disabled: true,
        dialogClass: 'dialog-width-md',

        /* --- Date --- */
        picker: null,
        dateId: null,
        formatted: null,
        dateFormatted: '',
        date: new Date().toISOString().substr(0, 10),
        renderTable: true,
        renderForm: true,
        datepicker: {
            closeOnContentClick: false
        },

        notify: {
            enabled: false,
            timeout: 5000,
            color: 'success',
            text: null,
            mode: '',
            y: 'top',
            x: null,
        },

        url: {
            area: window.location.pathname.split('/')[1],
            controller: window.location.pathname.split('/')[2]
        },

        /* --- X --- */
        xlsxColumns: [],
        zipName: null,
        zipCustomName: null
    },
    getters,
    mutations,
    actions,
    modules: {
      RootApp,
      RootEditProfile,
      RootAbout,
      RootAnnouncement,
      document,
      workflow,
      dashboard,
      tooltip,
      registration,
      application,
      conceptNote,
      grant,
      reviewForm,
      assessmentForm,
      reviewExternal,
      technicalReport,
      financialReport,
      communication,
      validation,
      migration,
      tracker,
      feedback,
      grantAmendment
    }
};

/*
    #. Override appliaction level settings to screen level settings
*/
for (const moduleName of Object.keys(modules)) {
    let rootState;
    rootState = store.modules;

    if (moduleName.indexOf('Index') != -1) {
        rootState[moduleName] = window.modules[moduleName];
    }
}

export default new Vuex.Store<any>(store);

/*
    #. Override appliaction level settings to screen level settings
*/

let i = 0;

for (const moduleName of Object.keys(modules)) {
    let rootState: any = {},
        componentName = moduleName.charAt(0).toUpperCase() + moduleName.slice(1);
    rootState = store.state;

    if(i == 0)
        store.state.dashboard = Object.assign({}, rootState, store.state.dashboard);

    rootState.moduleName = componentName.replace('Index', '') + 'Component';

    if (store.state[moduleName] == undefined) {
        rootState[moduleName] = window.modules[moduleName].state;
    }

    if (store.state.url.controller == componentName) {
        rootState.dashboard.grandParent = rootState.moduleName;
    }

    if (store.state[moduleName] && store.state[moduleName].parentComponent) {
        rootState.dashboard.parentComponentId = moduleName;
    }

    if (store.state[moduleName]) {
        rootState.fileName = moduleName;
        store.state[moduleName] = Object.assign({}, rootState, store.state[moduleName]);
    }

    i++;
}